/**
 * VacancyCalendar — mini calendar SVG showing the month of the "next vacancy" date.
 *
 * Layout:
 *   - Teal header bar with month name + year
 *   - Day-of-week initials row
 *   - 6×7 grid of date squares (tight grid-like spacing)
 *   - In-month dates: pale blue; out-of-month: dim inactive
 *   - Only the vacancy day shows its number (inside a gold highlight circle)
 *   - Uses top 4-6 rows depending on the month, remaining rows are inactive
 *
 * Uses theme colors: teal, cream, dark.
 */

import React from 'react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DOW_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// ─── Dimensions (tight grid-like layout, 75% of original) ──
const COL_COUNT = 7;
const ROWS = 6; // always 6 rows rendered
const CELL_W = 13.5;
const CELL_H = 10.5;
const RECT_W = 12;
const RECT_H = 9;
const GRID_W = COL_COUNT * CELL_W;                // ~94.5
const PAD_X = 4;
const SVG_W = Math.round(GRID_W + PAD_X * 2);     // ~103

const HEADER_H = 15;
const DOW_H = 10.5;
const PAD_TOP = 3;
const PAD_BOT = 3;

/** Exported so ContentRunwayIndicator can match circle height to calendar SVG height */
export const CALENDAR_SVG_H = Math.round(PAD_TOP + HEADER_H + DOW_H + ROWS * CELL_H + PAD_BOT); // ~95

const COLORS = {
  bg: '#262626',
  border: '#3a3630',
  header: '#1f7d7e',
  cream: '#f7e5c5',
  inMonth: 'rgba(100, 165, 195, 0.30)',   // pale blue
  outMonth: 'rgba(247, 229, 197, 0.06)',   // dim inactive
  highlight: '#e6b832',
};

interface VacancyCalendarProps {
  /** ISO date string (YYYY-MM-DD) of the next vacancy */
  vacancyDate: string;
}

const VacancyCalendar: React.FC<VacancyCalendarProps> = ({ vacancyDate }) => {
  const [vyStr, vmStr, vdStr] = vacancyDate.split('-');
  const vy = Number(vyStr);
  const vm = Number(vmStr) - 1; // 0-indexed month
  const vd = Number(vdStr);

  const daysInMonth = new Date(vy, vm + 1, 0).getDate();
  const firstDow = new Date(vy, vm, 1).getDay(); // 0=Sun

  const headerY = PAD_TOP;
  const dowY = headerY + HEADER_H;
  const gridY = dowY + DOW_H;

  return (
    <svg
      width={SVG_W}
      height={CALENDAR_SVG_H}
      viewBox={`0 0 ${SVG_W} ${CALENDAR_SVG_H}`}
      style={{ display: 'block' }}
    >
      {/* Background */}
      <rect x={0} y={0} width={SVG_W} height={CALENDAR_SVG_H} rx={5} fill={COLORS.bg} stroke={COLORS.border} strokeWidth={1} />

      {/* Month header */}
      <rect x={1} y={headerY} width={SVG_W - 2} height={HEADER_H} rx={4} fill={COLORS.header} />
      <text
        x={SVG_W / 2}
        y={headerY + HEADER_H / 2 + 3}
        fill={COLORS.cream}
        fontFamily="Inter, sans-serif"
        fontSize={8}
        fontWeight={700}
        textAnchor="middle"
        style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {MONTH_NAMES[vm]} {vy}
      </text>

      {/* Day-of-week labels */}
      {DOW_LABELS.map((label, i) => (
        <text
          key={`dow-${i}`}
          x={PAD_X + i * CELL_W + CELL_W / 2}
          y={dowY + DOW_H / 2 + 2.5}
          fill={COLORS.cream}
          fontFamily="Inter, sans-serif"
          fontSize={7}
          fontWeight={600}
          textAnchor="middle"
          opacity={0.4}
        >
          {label}
        </text>
      ))}

      {/* Day grid — always 6 rows × 7 columns */}
      {Array.from({ length: ROWS }, (_, row) =>
        Array.from({ length: COL_COUNT }, (__, col) => {
          const cellIdx = row * COL_COUNT + col;
          const dayNum = cellIdx - firstDow + 1;
          const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
          const isVacancy = inMonth && dayNum === vd;

          const cx = PAD_X + col * CELL_W + CELL_W / 2;
          const cy = gridY + row * CELL_H + CELL_H / 2;
          const rx = cx - RECT_W / 2;
          const ry = cy - RECT_H / 2;

          return (
            <React.Fragment key={`d-${row}-${col}`}>
              {/* Colored square — in-month (pale blue) vs out-of-month (inactive) */}
              {!isVacancy && (
                <rect
                  x={rx}
                  y={ry}
                  width={RECT_W}
                  height={RECT_H}
                  rx={1}
                  fill={inMonth ? COLORS.inMonth : COLORS.outMonth}
                />
              )}
              {/* Vacancy day — gold circle with number (kept at original size) */}
              {isVacancy && (
                <>
                  <circle
                    cx={cx}
                    cy={cy}
                    r={7}
                    fill={COLORS.highlight}
                    stroke={COLORS.cream}
                    strokeWidth={1}
                  />
                  <text
                    x={cx}
                    y={cy + 3.5}
                    fill="#262626"
                    fontFamily="Inter, sans-serif"
                    fontSize={10}
                    fontWeight={700}
                    textAnchor="middle"
                  >
                    {vd}
                  </text>
                </>
              )}
            </React.Fragment>
          );
        })
      )}
    </svg>
  );
};

export default React.memo(VacancyCalendar);
